import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Components
import {
  U21Avatar,
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconUser } from '@u21/tabler-icons';

// Selectors
import { selectHasReadAgentsPermissions } from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatAgentOptions } from 'app/modules/search/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { AGENT_QUERY_KEYS } from 'app/modules/agents/queries/keys';
import { AgentStatus } from 'app/modules/agents/models';
import {
  searchAgent,
  useAgentSearch,
} from 'app/modules/agents/queries/useAgentSearch';
import { PaginatedAgentsPayload } from 'app/modules/agents/requests';
import { DEFAULT_MIN_PAGINATION_LIMIT } from 'app/shared/pagination/constants';

export type U21AgentSearchProps<TClearable extends boolean> = DistributiveOmit<
  U21SelectSearchProps<number, TClearable>,
  'options' | 'onSearch'
> & {
  allowedStatuses?: AgentStatus[];
  allowedQueueIds?: number[];
  unassigned?: boolean;
};

const UNASSIGNED_OPTION = {
  icon: <U21Avatar name="?" />,
  text: 'Unassigned',
  value: -1,
};

export const U21AgentSearch = <TClearable extends boolean>({
  allowedStatuses,
  allowedQueueIds,
  unassigned = false,
  ...rest
}: U21AgentSearchProps<TClearable>) => {
  const { value } = rest;
  const queryClient = useQueryClient();

  const [payload, setPayload] = useState<PaginatedAgentsPayload>({
    limit: DEFAULT_MIN_PAGINATION_LIMIT,
    offset: OFFSET_DEFAULT,
    statuses: allowedStatuses,
    queue_ids: allowedQueueIds,
  });
  const [searchValue, setSearchValue] = useState<string>('');

  const hasReadAgentsPermissions = useSelector(selectHasReadAgentsPermissions);

  const {
    agents: data,
    query: { isFetching: loading },
    refreshAgents,
  } = useAgentSearch(payload);

  const formattedData = useMemo(() => formatAgentOptions(data), [data]);

  const options = useMemo(
    () => (unassigned ? [UNASSIGNED_OPTION, ...formattedData] : formattedData),
    [formattedData, unassigned],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadAgentsPermissions) {
      return;
    }
    let missingValues: number[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }

    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;

      const initialPayload: PaginatedAgentsPayload = {
        limit: missingValues.length,
        offset: OFFSET_DEFAULT,
        ids: missingValues.map(Number),
        statuses: allowedStatuses,
        queue_ids: allowedQueueIds,
      };

      queryClient.prefetchQuery({
        queryKey: AGENT_QUERY_KEYS.getAgentSearch(initialPayload),
        queryFn: () => searchAgent(initialPayload),
      });

      setPayload(initialPayload);
    }
  }, [
    allowedQueueIds,
    allowedStatuses,
    hasReadAgentsPermissions,
    options,
    queryClient,
    value,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        setPayload({
          limit: INFINITE_SCROLL_LIMIT,
          offset: OFFSET_DEFAULT,
          query: [query],
          statuses: allowedStatuses,
          queue_ids: allowedQueueIds,
        });
      }
    },
    [allowedQueueIds, allowedStatuses],
  );

  if (!hasReadAgentsPermissions) {
    return null;
  }

  return (
    <U21SelectSearch
      loading={loading}
      options={options}
      onExactSearch={async (searchTerms) => {
        const initialPayload: PaginatedAgentsPayload = {
          limit: searchTerms.length,
          offset: OFFSET_DEFAULT,
          ids: searchTerms.map(Number).filter((i) => !isNaN(i)),
          statuses: allowedStatuses,
          queue_ids: allowedQueueIds,
        };

        await Promise.all([
          // get IDs
          queryClient.prefetchQuery({
            queryKey: AGENT_QUERY_KEYS.getAgentSearch(initialPayload),
            queryFn: () => searchAgent(initialPayload),
          }),

          // get names
          ...searchTerms.map((i) => {
            const searchPayload: PaginatedAgentsPayload = {
              limit: searchTerms.length,
              offset: OFFSET_DEFAULT,
              statuses: allowedStatuses,
              queue_ids: allowedQueueIds,
              query: [i],
            };
            return queryClient.prefetchQuery({
              queryKey: AGENT_QUERY_KEYS.getAgentSearch(searchPayload),
              queryFn: () => searchAgent(searchPayload),
            });
          }),
        ]);

        refreshAgents();
      }}
      onSearch={onSearch}
      startIcon={<IconUser />}
      noOptionsText={
        searchValue
          ? 'No results found'
          : 'Type to search or paste a list of agent IDs or names'
      }
      {...rest}
    />
  );
};
